import { makeStyles } from '@mui/styles';
import TreeView from 'apollo-react/components/TreeView';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RenderTreeItems from './RenderTreeItems';
import { getSelectedItemPath, isSelectable, isShowWarningModal, mapNodes } from './utils';
import { getDefaultDictionary } from '../../../actions/RegulatoryTermsActions';
import Loader from '../../../components/CustomLoader';
import { useTranslation } from '../../../custom-hooks/useTranslation';

const useStyles = makeStyles({
  loadingContainer: {
    position: 'absolute',
    top: 0,
    width: '95%',
    height: '100%',
    background: 'rgb(255,255,255,0.8)',
  },
});

// CDRH or IMDRF
const DefaultTree = ({
  onSelectItem,
  termHierarchy = '',
  allExpanded = false,
  searchText = '',
  type,
  regulatoryTerms,
  handleIMDRFSelect,
}) => {
  const [expandedNodes, setExpandedNodes] = useState([]);
  const {
    defaultDictionary: dictionary,
    defaultFilteredDictionary: filteredDictionary,
    loading,
    hasError,
  } = useSelector(({ regulatoryDictionaries }) => regulatoryDictionaries);
  const dispatch = useDispatch();
  const classes = useStyles();
  const translateString = useTranslation();

  const items = useMemo(() => {
    return dictionary.reduce(mapNodes(), {});
  }, [dictionary]);

  const filteredItems = useMemo(() => {
    return filteredDictionary.reduce(mapNodes(), {});
  }, [filteredDictionary]);

  useEffect(() => {
    fetchDictionary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allExpanded || !_.isEmpty(searchText)) {
      if (!_.isEmpty(items)) {
        const hasChildren = Object.values(items).filter((item) => !_.isEmpty(item.Children));
        setExpandedNodes(hasChildren.map((item) => item.Id));
      } else if (!_.isEmpty(filteredItems)) {
        const hasChildren = Object.values(filteredItems).filter(
          (item) => !_.isEmpty(item.Children)
        );
        setExpandedNodes(hasChildren.map((item) => item.Id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExpanded, items, filteredItems]);

  const fetchDictionary = () => {
    const showAll = _.isEmpty(searchText);

    const data = {
      HierarchyId: '',
      TextSearchOption: 0,
      TermHierarchy: termHierarchy,
      Dictionary: regulatoryTerms,
      SearchText: showAll ? '' : searchText,
      SearchBy: 'TERM/CODE',
      ShowAll: showAll,
    };

    selectItem(null);
    dispatch(getDefaultDictionary(data, !showAll));
  };

  const selectItem = (node, isDoubleClicked) => {
    let selectedItemPath = null;
    let selectedItemNode = null;

    if (node) {
      const { Code: code, Name: name } = node;
      const selectable = isSelectable(node, type);
      const showWarningModal = isShowWarningModal(node);

      selectedItemPath = getSelectedItemPath(node);
      selectedItemNode = { name, code, selectable, showWarningModal };
      if (selectable && isDoubleClicked) {
        handleIMDRFSelect();
        return;
      }
    }

    onSelectItem(selectedItemNode, selectedItemPath);
  };

  const handleSelect = (event, nodeId) => {
    const node = items[nodeId] || filteredItems[nodeId];
    const isDoubleClicked = event.detail === 2;

    selectItem(node, isDoubleClicked);
  };

  const handleExpandedNodes = (_, nodeIds) => setExpandedNodes(nodeIds);

  const renderDictionary = _.isEmpty(filteredDictionary) ? dictionary : filteredDictionary;

  return loading ? (
    <div className={classes.loadingContainer}>
      <Loader />
    </div>
  ) : hasError ? (
    <div>{translateString('No data to display')}</div>
  ) : (
    <TreeView
      onNodeToggle={handleExpandedNodes}
      expanded={expandedNodes}
      onNodeSelect={handleSelect}
    >
      {renderDictionary.map((node, index) => (
        <RenderTreeItems key={index} node={node} type={type} />
      ))}
    </TreeView>
  );
};

export default DefaultTree;
